import { useEffect, useState } from 'react'
import {
  ROAD_WIDTH_RAMP, RAIL_WIDTH_RAMP, CITY_RADIUS_RAMP,
  scaleRamp, dashArrayFor, cityMarkerImage,
} from '../../utils/overlayStyles'

// Real-world reference overlays (cities / roads / railways). Static Natural-Earth
// data, lazy-fetched from /data/overlays the first time each toggle is enabled
// (never at page load), cached in state, then handed to MapLibre which tiles them
// client-side. Owns: the fetch, the layer creation, and live restyling.
export function useWorldOverlays(map, { showCities, showRoads, showRailways, styles, regionsData }) {
  const [worldOverlays, setWorldOverlays] = useState({ cities: null, roads: null, railways: null })

  // Lazy fetch (static assets — same-origin, not the API layer).
  useEffect(() => {
    const need = []
    if (showRoads && !worldOverlays.roads) need.push('roads')
    if (showRailways && !worldOverlays.railways) need.push('railways')
    if (showCities && !worldOverlays.cities) need.push('cities')
    if (!need.length) return

    let cancelled = false
    Promise.all(
      need.map((key) =>
        fetch(`/data/overlays/${key}.geojson`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => [key, d])
          .catch(() => [key, null])
      )
    ).then((pairs) => {
      if (cancelled) return
      setWorldOverlays((prev) => {
        const next = { ...prev }
        pairs.forEach(([key, data]) => { if (data) next[key] = data })
        return next
      })
    })
    return () => { cancelled = true }
  }, [showRoads, showRailways, showCities, worldOverlays])

  // Add / update / toggle the world-overlay layers. Roads and railways sit just
  // beneath the RP structure icons; city dots + labels ride on top. Per-layer
  // minzoom keeps the dense line meshes off the GPU at world view, and the
  // cities filter reveals them progressively by precomputed per-city `minzoom`.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return
    const beneathRP = map.current.getLayer('structures-points')
      ? 'structures-points'
      : (map.current.getLayer('regions-hover') ? 'regions-hover' : undefined)

    // Roads
    if (worldOverlays.roads && !map.current.getSource('world-roads')) {
      map.current.addSource('world-roads', { type: 'geojson', data: worldOverlays.roads })
      map.current.addLayer({
        id: 'world-roads',
        type: 'line',
        source: 'world-roads',
        minzoom: 2,
        layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
        paint: {
          'line-color': styles.roads.color,
          'line-width': ['interpolate', ['linear'], ['zoom'], ...scaleRamp(ROAD_WIDTH_RAMP, styles.roads.width)],
          'line-dasharray': dashArrayFor(styles.roads.dash),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0, 3.5, 0.7],
        },
      }, beneathRP)
    }
    if (map.current.getLayer('world-roads')) {
      map.current.setLayoutProperty('world-roads', 'visibility', showRoads ? 'visible' : 'none')
    }

    // Railways (dashed, cooler tone to read distinctly from roads)
    if (worldOverlays.railways && !map.current.getSource('world-railways')) {
      map.current.addSource('world-railways', { type: 'geojson', data: worldOverlays.railways })
      map.current.addLayer({
        id: 'world-railways',
        type: 'line',
        source: 'world-railways',
        minzoom: 2,
        layout: { 'line-cap': 'butt', 'line-join': 'round', visibility: 'none' },
        paint: {
          'line-color': styles.railways.color,
          'line-width': ['interpolate', ['linear'], ['zoom'], ...scaleRamp(RAIL_WIDTH_RAMP, styles.railways.width)],
          'line-dasharray': dashArrayFor(styles.railways.dash),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0, 3.5, 0.75],
        },
      }, beneathRP)
    }
    if (map.current.getLayer('world-railways')) {
      map.current.setLayoutProperty('world-railways', 'visibility', showRailways ? 'visible' : 'none')
    }

    // Cities: dots + labels, progressively revealed by per-feature minzoom.
    const cityFilter = ['<=', ['get', 'minzoom'], ['zoom']]
    if (worldOverlays.cities && !map.current.getSource('world-cities')) {
      map.current.addSource('world-cities', { type: 'geojson', data: worldOverlays.cities })
      map.current.addLayer({
        id: 'world-cities-dots',
        type: 'circle',
        source: 'world-cities',
        filter: cityFilter,
        layout: { visibility: 'none' },
        paint: {
          'circle-color': styles.cities.color,
          'circle-stroke-color': '#1a1a1a',
          'circle-stroke-width': 1,
          'circle-radius': ['interpolate', ['linear'], ['get', 'pop'], ...scaleRamp(CITY_RADIUS_RAMP, styles.cities.width)],
        },
      })
      // Non-circle marker shapes (square / triangle / star) render through this
      // symbol layer using a canvas-generated image regenerated on colour/shape
      // change (icon tinting needs SDF images; regenerating the bitmap in the
      // right colour is simpler and just as fast at this size).
      map.current.addLayer({
        id: 'world-cities-shapes',
        type: 'symbol',
        source: 'world-cities',
        filter: cityFilter,
        layout: {
          visibility: 'none',
          'icon-image': 'city-marker',
          'icon-allow-overlap': true,
          'icon-size': ['interpolate', ['linear'], ['get', 'pop'], 100000, 0.35, 1000000, 0.5, 5000000, 0.7, 20000000, 1],
        },
      })
      map.current.addLayer({
        id: 'world-cities-labels',
        type: 'symbol',
        source: 'world-cities',
        filter: cityFilter,
        layout: {
          visibility: 'none',
          'text-field': ['get', 'name'],
          'text-size': ['interpolate', ['linear'], ['get', 'pop'], 100000, 10, 5000000, 14, 20000000, 17],
          'text-anchor': 'left',
          'text-offset': [0.6, 0],
          'text-optional': true,
        },
        paint: {
          'text-color': '#f4e4bc',
          'text-halo-color': '#1a1a1a',
          'text-halo-width': 1.4,
        },
      })
    }
    const isCircle = (styles.cities.shape || 'circle') === 'circle'
    if (map.current.getLayer('world-cities-dots')) {
      map.current.setLayoutProperty('world-cities-dots', 'visibility', showCities && isCircle ? 'visible' : 'none')
    }
    if (map.current.getLayer('world-cities-shapes')) {
      map.current.setLayoutProperty('world-cities-shapes', 'visibility', showCities && !isCircle ? 'visible' : 'none')
    }
    if (map.current.getLayer('world-cities-labels')) {
      map.current.setLayoutProperty('world-cities-labels', 'visibility', showCities ? 'visible' : 'none')
    }
  }, [map, worldOverlays, showCities, showRoads, showRailways, regionsData, styles.cities.shape])

  // Live restyle of the world overlays when the user edits colour/width/dash/
  // shape — separate from creation so it re-applies to already-added layers too.
  useEffect(() => {
    if (!map.current) return
    if (map.current.getLayer('world-roads')) {
      map.current.setPaintProperty('world-roads', 'line-color', styles.roads.color)
      map.current.setPaintProperty('world-roads', 'line-width',
        ['interpolate', ['linear'], ['zoom'], ...scaleRamp(ROAD_WIDTH_RAMP, styles.roads.width)])
      map.current.setPaintProperty('world-roads', 'line-dasharray', dashArrayFor(styles.roads.dash))
    }
    if (map.current.getLayer('world-railways')) {
      map.current.setPaintProperty('world-railways', 'line-color', styles.railways.color)
      map.current.setPaintProperty('world-railways', 'line-width',
        ['interpolate', ['linear'], ['zoom'], ...scaleRamp(RAIL_WIDTH_RAMP, styles.railways.width)])
      map.current.setPaintProperty('world-railways', 'line-dasharray', dashArrayFor(styles.railways.dash))
    }
    if (map.current.getLayer('world-cities-dots')) {
      map.current.setPaintProperty('world-cities-dots', 'circle-color', styles.cities.color)
      map.current.setPaintProperty('world-cities-dots', 'circle-radius',
        ['interpolate', ['linear'], ['get', 'pop'], ...scaleRamp(CITY_RADIUS_RAMP, styles.cities.width)])
      // Circle scaling via radius ramp; the shape marker scales via icon-size.
      if (map.current.getLayer('world-cities-shapes')) {
        map.current.setLayoutProperty('world-cities-shapes', 'icon-size',
          ['interpolate', ['linear'], ['get', 'pop'],
            100000, 0.35 * styles.cities.width, 1000000, 0.5 * styles.cities.width,
            5000000, 0.7 * styles.cities.width, 20000000, 1 * styles.cities.width])
      }
    }
    // (Re)generate the marker image for the current shape+colour.
    const shape = styles.cities.shape || 'circle'
    if (shape !== 'circle' && map.current.getLayer('world-cities-shapes')) {
      const img = cityMarkerImage(shape, styles.cities.color)
      if (map.current.hasImage('city-marker')) map.current.updateImage('city-marker', img)
      else map.current.addImage('city-marker', img)
    }
  }, [map, styles.roads.color, styles.roads.width, styles.roads.dash, styles.railways.color, styles.railways.width, styles.railways.dash, styles.cities.color, styles.cities.width, styles.cities.shape, worldOverlays])

  return worldOverlays
}
